import {
  FileFieldDisplayType,
  RelationshipFieldDisplayType,
  StandardFieldDatabaseType,
  StandardFieldDisplayType,
} from "@/types/breeze/Field";
import FileCellRenderer from "../grid/FileCellRenderer";
import {
  ILargeTextEditorParams,
  INumberCellEditorParams,
  ISelectCellEditorParams,
} from "ag-grid-community";
import { DataGridColumn } from "./DataGrid";
import {
  EnhancedJsonCellEditor,
  MultiSelectCellRenderer,
} from "./DataGrid.editors";

const systemFields = ["id", "created_at", "updated_at", "locked", "tenant_id"];

export const getCellRenderer = (column: DataGridColumn<any>) => {
  const displayType = column.fieldType?.display_type;

  switch (displayType) {
    case StandardFieldDisplayType.CHECKBOX:
    case StandardFieldDisplayType.RADIO:
      return {
        cellRenderer: "agCheckboxCellRenderer",
      };

    case FileFieldDisplayType.FILE:
      return {
        cellRenderer: FileCellRenderer,
      };

    case RelationshipFieldDisplayType.RELATIONSHIP:
      return {
        cellClass: "relationship-cell",
      };

    case StandardFieldDisplayType.SELECT:
      // Check if it's multi-select
      if (column.fieldType?.isMultiSelect) {
        return {
          cellRenderer: MultiSelectCellRenderer,
          cellRendererParams: {
            selectOptions: column.fieldType?.selectOptions || [],
          },
          editable: false,
          suppressKeyboardEvent: () => true,
          cellClass: "multi-select-cell",
        };
      }
  }

  return {};
};

export const getCellEditor = (column: DataGridColumn<any>) => {
  const displayType = column.fieldType?.display_type;
  const databaseType = column.fieldType?.database_type;

  if (systemFields.includes(column.field)) {
    return {
      editable: false,
    };
  }

  switch (displayType) {
    case StandardFieldDisplayType.CHECKBOX:
    case StandardFieldDisplayType.RADIO:
      return {
        cellEditor: "agCheckboxCellEditor",
        editable: true,
      };

    case FileFieldDisplayType.FILE:
      return {
        editable: false,
      };

    case RelationshipFieldDisplayType.RELATIONSHIP:
      return {
        editable: false,
        suppressKeyboardEvent: () => true,
      };

    case StandardFieldDisplayType.JSON:
      return {
        cellEditor: EnhancedJsonCellEditor,
        cellEditorPopup: true,
        editable: true,
        suppressKeyboardEvent: (params: { event: KeyboardEvent }) => {
          const event = params.event;
          // Suppress AG Grid handling for Shift+Enter so Monaco can create new lines
          if (event.key === "Enter" && event.shiftKey) {
            return true;
          }
          return false;
        },
      };

    case StandardFieldDisplayType.TEXTAREA:
    case StandardFieldDisplayType.RICH_TEXT:
      return {
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        editable: true,
        cellEditorParams: {
          rows: 10,
          cols: 50,
          maxLength: 2000,
        } as ILargeTextEditorParams,
      };

    case StandardFieldDisplayType.SELECT:
      // Check if it's multi-select
      if (column.fieldType?.isMultiSelect) {
        return {
          cellRenderer: MultiSelectCellRenderer,
          cellRendererParams: {
            selectOptions: column.fieldType?.selectOptions || [],
          },
          editable: false,
          suppressKeyboardEvent: () => true,
        };
      }
      return {
        cellEditor: "agSelectCellEditor",
        editable: true,
        cellEditorParams: {
          values:
            column.fieldType?.selectOptions?.map((opt) => opt.value) || [],
        } as ISelectCellEditorParams,
      };

    case StandardFieldDisplayType.TIMESTAMP:
    case StandardFieldDisplayType.SHORT_DATE:
    case StandardFieldDisplayType.LONG_DATE:
      return {
        cellEditor: "agDateStringCellEditor",
        editable: true,
        cellEditorParams: {
          min: "1900-01-01",
          max: "2100-12-31",
          includeTime: displayType === StandardFieldDisplayType.TIMESTAMP,
        },
      };
  }

  // Check database type for number fields
  if (
    databaseType === StandardFieldDatabaseType.INTEGER ||
    databaseType === StandardFieldDatabaseType.BIGINTEGER
  ) {
    return {
      cellEditor: "agNumberCellEditor",
      editable: true,
      cellEditorParams: {
        precision: 0,
        step: 1,
        min: -2147483648,
        max: 2147483647,
      } as INumberCellEditorParams,
    };
  }

  if (databaseType === StandardFieldDatabaseType.DECIMAL) {
    return {
      cellEditor: "agNumberCellEditor",
      editable: true,
      cellEditorParams: {
        precision: 2,
        step: 0.01,
      } as INumberCellEditorParams,
    };
  }

  // Default to text editor
  return {
    cellEditor: "agTextCellEditor",
    editable: true,
    cellEditorParams: {
      maxLength: 524288,
    },
  };
};
